import {
  AutocompleteInput,
  Edit,
  FormDataConsumer,
  ReferenceInput,
  SaveButton,
  SimpleForm,
  Toolbar,
  useNotify,
  usePermissions,
  useRefresh,
  type RaRecord,
} from "react-admin";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import MuiButton from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { hasPermission } from "@/utils/permission-utils";
import { buildSadaratCreateUrl } from "@/utils/route-utility";
import { toSadaratPartialUpdate } from "@/utils/raza-event-sadarat-update";
import type { MissingSadaratGenderScope } from "@/utils/missing-sadarat-gender-scope";

function PartialEditToolbar({ onCancel }: { onCancel: () => void }) {
  return (
    <Toolbar
      sx={{
        bgcolor: "transparent",
        boxShadow: "none",
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
        px: { xs: 0, sm: 2 },
        mt: { xs: 2, sm: 1 },
        minHeight: "auto",
        flexWrap: "wrap",
      }}
    >
      <MuiButton type="button" onClick={onCancel}>
        Cancel
      </MuiButton>
      <SaveButton />
    </Toolbar>
  );
}

function SadaratSlotInput({
  source,
  gender,
  label,
}: {
  source: "sadaratMaleId" | "sadaratFemaleId";
  gender: "MALE" | "FEMALE";
  label: string;
}) {
  return (
    <ReferenceInput
      source={source}
      reference="sadarats"
      perPage={100}
      allowEmpty
      filter={{ gender }}
    >
      <AutocompleteInput optionText="name" label={label} emptyText="— None —" fullWidth />
    </ReferenceInput>
  );
}

/** Assign / change male and/or female sadarat on a raza event (Allocate queue + event cards). */
export function ChangeSadaratDialog({
  open,
  onClose,
  majlisId,
  focusGender = "all",
}: {
  open: boolean;
  onClose: () => void;
  majlisId: string | number;
  /** When set from Allocate tabs, scopes which pickers to show. */
  focusGender?: MissingSadaratGenderScope;
}) {
  const refresh = useRefresh();
  const notify = useNotify();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const { permissions } = usePermissions();
  const canCreateSadarat = hasPermission(permissions, "sadarats.create");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isNarrow}>
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2 }, pb: 1 }}>
        Change sadarat assignment
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          pb: { xs: 2, sm: 3 },
          pt: { xs: 1, sm: 2 },
          "& .RaEdit-main": { mt: 0 },
          "& .RaSimpleForm-root": { pt: 0 },
          "& .MuiFormControl-root": { mt: { xs: 1, sm: 0 } },
        }}
      >
        <Edit
          key={`sadarat-${majlisId}-${focusGender}`}
          resource="raza-events"
          id={majlisId}
          mutationMode="pessimistic"
          redirect={false}
          actions={false}
          transform={toSadaratPartialUpdate}
          mutationOptions={{
            onSuccess: (data: RaRecord) => {
              const warnings = data?.warnings;
              if (Array.isArray(warnings) && warnings.length > 0) {
                notify(warnings.filter((w): w is string => typeof w === "string").join(" "), {
                  type: "warning",
                  multiLine: true,
                });
              } else {
                notify("Sadarat assignment saved", { type: "success" });
              }
              refresh();
              onClose();
            },
          }}
        >
          <SimpleForm toolbar={<PartialEditToolbar onCancel={onClose} />}>
            <FormDataConsumer<RaRecord>>
              {({ formData }) => {
                const needMale = Boolean(formData?.needSadaratMale);
                const needFemale = Boolean(formData?.needSadaratFemale);
                const showMale = needMale && focusGender !== "female";
                const showFemale = needFemale && focusGender !== "male";
                return (
                  <>
                    {showMale ? (
                      <SadaratSlotInput source="sadaratMaleId" gender="MALE" label="Male sadarat" />
                    ) : null}
                    {showFemale ? (
                      <SadaratSlotInput
                        source="sadaratFemaleId"
                        gender="FEMALE"
                        label="Female sadarat"
                      />
                    ) : null}
                    {!showMale && !showFemale ? (
                      <Typography variant="body2" color="text.secondary">
                        This event does not need a sadarat assignment for the selected gender.
                      </Typography>
                    ) : null}
                  </>
                );
              }}
            </FormDataConsumer>
            {canCreateSadarat ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: { xs: 2, sm: 1.5 } }}>
                Need a new entry?{" "}
                <Link
                  href={buildSadaratCreateUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                >
                  Add sadarat
                </Link>
              </Typography>
            ) : null}
          </SimpleForm>
        </Edit>
      </DialogContent>
    </Dialog>
  );
}
